import Asset from "mibaby-frontend/media/Asset";
import React, { PureComponent, useMemo } from "react";
import ClickTracker, { trackRaw } from "@components/click-tracker";
import { HtmlToReact } from "@components/html";
import { SocialButtons } from "./SocialButtons";
import matchDetailLink from "@components/content-feed/util/matchDetailLink";
import renderDetailLink from "@components/content-feed/util/renderDetailLink";

const getButtonClass = (style, className) => {
  return {
    PRIMARY: "btn btn-block btn-primary " + className,
    SECONDARY: "btn btn-block btn-secondary " + className,
    DEFAULT: "btn btn-block btn-default " + className,
  }[style];
};

const AnswerButton = ({ answer, style = "PRIMARY", onChoose, className, selected, disabled }) => (
  <button
    disabled={disabled}
    className={getButtonClass(style, className)}
    onClick={() => onChoose()}
  >
    {answer}
  </button>
);

const AnswerDropdown = ({ answer_options, onChoose, selected, disabled }) => (
    <select value={selected || ""} onChange={e => onChoose(e.target.value)} disabled={typeof selected !== "undefined" ? selected : disabled}>
        {answer_options.map(o => (
            <option key={o}>{o}</option>
        ))}
    </select>
);

const Answer = ({ display_type, ...props }) => {
  switch (display_type) {
    case "button":
      return <AnswerButton {...props} />;
    case "dropdown":
      return <AnswerDropdown {...props} />;
  }
};

const Decision = ({ value }) => (
  <div className="content-feed-question-decision clearfix animated fadeIn">
    <div className="panel pull-right">
      <div className="panel-body">{value}</div>
    </div>
  </div>
);

const Question = ({ question, answers, selected, expert, asset, next }) => (
  <div className={`content-feed-question${expert ? " has-expert" : ""}`}>
    <div className="panel animated fadeIn">
      {expert && (
        <figure className="text-center">
          <img
            className="content-feed-question-expert img-thumbnail img-circle img-pull-up"
            src={`/img/experts/${expert.picture}`}
          />
        </figure>
      )}
      {asset && <Asset {...asset} className="content-feed-question-image" />}
      <div className="panel-body">
        <HtmlToReact inline html={question} className="text-bold text-center" />
        <div className="row">
          {!selected &&
            answers &&
            answers.map(answer => {
              let isSelected = null;
              if (answer.display_type === "button" && answer.answer === selected) {
                isSelected = true;
              }
              if (answer.display_type === "dropdown" && selected) {
                isSelected = selected;
              }

              const onChoose = {
                button: () => next(answer),
                dropdown: value => next(answer, value),
              }[answer.display_type];
              const col = answers.length <= 3 ? 12 / answers.length : 6;

              return (
                <div className={`col-xs-${col} content-feed-question-answer`} key={answer.id}>
                  <ClickTracker method="answer" id={answer.id}>
                    <Answer
                      {...answer}
                      selected={isSelected}
                      disabled={!!selected}
                      onChoose={onChoose}
                    />
                  </ClickTracker>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  </div>
);

// its not a popup! :)
const Popup = ({ title, text, asset, links = [], onLinkClick }) => (
  <div className="content-feed-question-popup panel animated fadeIn">
    {asset && <Asset {...asset} />}
    {(title || text) && (
      <div className="panel-body">
        {title && <div className="text-bold">{title}</div>}
        {text && <HtmlToReact preset="basic" html={text} />}
      </div>
    )}
    {links.length > 0 && (
      <div className="list-group">
        {links.map(({ resource, text }) => (
          <a key={resource} href={resource} onClick={onLinkClick} className="list-group-item">
            {text}
          </a>
        ))}
      </div>
    )}
  </div>
);

const Typing = () => (
  <div className="content-feed-question-typing anmiated fadeIn pull-left">
    <div className="panel">
      <div className="panel-body">
        <img src="/img/typing.svg" />
      </div>
    </div>
  </div>
);

const DefaultLast = ({ onReset }) => {
  return (
    <button className="btn-link pull-right" onClick={onReset}>
      Von vorne beginnen
    </button>
  );
};

const StackItem = ({
  item,
  selected,
  next,
  onReset,
  handleOutclick,
  LastComponent = DefaultLast,
}) => {
  const { type } = item;
  switch (type) {
    case "question":
      return (
        <Question
          next={next}
          selected={selected}
          question={item.question}
          answers={item.answers}
          expert={item.expert}
          asset={item.asset}
        />
      );
    case "decision":
      return <Decision value={item.value} />;
    case "typing":
      return <Typing />;
    case "popup":
      return <Popup {...item} onLinkClick={handleOutclick} />;
    case "last":
      return <LastComponent onReset={onReset} />;
    default:
      return <span>unkown</span>;
  }
};

class Stack extends PureComponent {
  constructor(props) {
    super(props);
    this.timeouts = [];
    this.state = Stack.getInitialState(props);
  }

  resetStack() {
    this.setState(Stack.getInitialState(this.props));
  }

  static defaultProps = {
    handleRedirect: (href, selectedValue) =>
      void (document.location = href.replace("{option}", selectedValue)),
    handleOutclick: link => {},
  };

  static getInitialState(props) {
    return {
      items: [
        {
          id: props.id,
          type: "question",
          question: props.question,
          answers: props.answers,
          expert: props.expert,
          asset: props.asset_mimetype && {
            mimeType: props.asset_mimetype,
            versions: props.asset_versions,
          },
        },
      ],
      current: props,
      decisions: {},
    };
  }

  static getDerivedStateFromProps(props, state) {
    const { items } = state;
    if (
      props.question !== items[0].question ||
      props.answers !== items[0].answers ||
      props.expert !== items[0].expert
    ) {
      return Stack.getInitialState(props);
    }
    return null;
  }

  componentWillUnmount() {
    this.timeouts.forEach(t => clearTimeout(t));
  }

  createTyping(fn, index) {
    return new Promise(resolve => {
      this.timeouts.push(
        setTimeout(() => {
          this.setState(old => {
            return {
              ...old,
              items: Object.assign([...old.items], { [index]: fn() }),
            };
          }, resolve);
        }, 1000)
      );
    });
  }

  getNextByAnswer(answer, value, index) {
    switch (answer.type) {
      case "FORWARD":
        this.createTyping(
          () => ({
            type: "question",
            id: answer.id,
            question: answer.forward.question,
            answers: answer.forward.answers,
            expert: this.props.expert,
          }),
          index
        );
        break;
      case "REDIRECT":
        const { handleRedirect } = this.props;
        handleRedirect(answer.redirect_resource, value);
        return null;
      case "POPUP":
        this.createTyping(() => {
          return {
            type: "popup",
            id: answer.id,
            title: answer.popup_header,
            text: answer.popup_text,
            links: answer.popup_links,
            asset: answer.asset_mimetype && {
              mimeType: answer.asset_mimetype,
              versions: answer.asset_versions,
            },
          };
        }, index).then(() => {
          this.setState(old => ({
            ...old,
            items: [
              ...old.items,
              {
                type: "last",
                id: "last",
              },
            ],
          }));
        });
        break;
    }
    return {
      id: `typing-${answer.id}`,
      type: "typing",
    };
  }

  next(answer, value, id) {
    this.setState(old => {
      const nextAction = this.getNextByAnswer(answer, value, old.items.length + 1);
      if (!nextAction) {
        return old;
      }

      return {
        ...old,
        decisions: {
          ...old.decisions,
          [id]: value || answer.answer,
        },
        items: [
          ...old.items,
          {
            id: `user-${answer.id}`,
            type: "decision",
            value: value || answer.answer,
          },
          nextAction,
        ],
      };
    });
  }

  render() {
    const { items, current, decisions } = this.state;
    const { LastComponent } = this.props;

    return (
      <div className="content-feed-question-stack">
        {items.map(i => (
          <StackItem
            key={i.id}
            next={(answer, value) => this.next(answer, value, i.id)}
            item={i}
            selected={decisions[i.id]}
            onReset={() => this.resetStack()}
            handleOutclick={this.props.handleOutclick}
            LastComponent={LastComponent}
          />
        ))}
      </div>
    );
  }
}

const FeedStack = ({ AGG_LIKE, social_buttons = [], ...props }) => {
  const [, itemId] = props.id.split("_");
  const [, teaserId] = props.teaser_id.split("_");

  const getLinkHrefAndTrack = href => {
    const detail = matchDetailLink(href);
    if (null !== detail) {
      const { detailId, assetId } = detail;
      href = renderDetailLink({ detailId, assetId, teaserId, itemId });
    } else {
      trackRaw("interaction", {
        i: "feed_checkpoint",
        aid: itemId,
      });
    }

    return [href, detail];
  };

  /**
   * @param {string} href the link of the question redirect
   * @param {any} value the value of the answer
   */
  const handleRedirect = (href, value) => {
    [href] = getLinkHrefAndTrack(href);
    document.location = href.replace("{option}", value);
  };

  /**
   * @param {React.SyntheticEvent} ev
   */
  const handleOutclick = ev => {
    if (ev.target && ev.target instanceof HTMLAnchorElement) {
      const [href, detail] = getLinkHrefAndTrack(ev.target.getAttribute("href"));
      // if detail is set, then its a popup and potentially href changed. (due to itemId)
      if (detail) {
        ev.preventDefault();
        document.location.href = href;
      }
    }
  };

  const LastComponent = useMemo(() => {
    return ({ onReset }) => (
      <div className="content-feed-question-last text-center">
        {social_buttons.length > 0 && [
          <p>Hat Dir das gefallen?</p>,
          <SocialButtons
            id={props.id}
            teaserId={teaserId}
            buttons={social_buttons}
            AGG_LIKE={AGG_LIKE}
          />,
        ]}
        <button className="btn-link indent" onClick={onReset}>
          Von vorne beginnen
        </button>
      </div>
    );
  }, [social_buttons]);

  return (
    <Stack
      {...props}
      handleRedirect={handleRedirect}
      handleOutclick={handleOutclick}
      LastComponent={LastComponent}
    />
  );
};

export default FeedStack;
